import 'reflect-metadata'


import { effect, Injectable, signal, WritableSignal } from '@angular/core'

interface SyncSessionOptions {
  storageKey?: string
  prefix?: string
  default?: unknown
}

export const SESSION_PREFIX_KEY = Symbol('sessionStoragePrefix')

export function SessionStoragePrefix(prefix: string): ClassDecorator {
  return function (target: object): void {
    Reflect.defineMetadata(SESSION_PREFIX_KEY, prefix, target)
  }
}

export function SyncWithSessionStorage(
  options: SyncSessionOptions = {}
): PropertyDecorator {
  return function (target: object, propertyKey: string | symbol): void {
    const internalKey = Symbol(String(propertyKey))
    const propKeyString = String(propertyKey)

    const extracted = (
      classConstructor: new (...args: unknown[]) => unknown
    ): string => {
      const classPrefix: string | undefined = Reflect.getMetadata(
        SESSION_PREFIX_KEY,
        classConstructor
      )
      const prefix = options.prefix ?? classPrefix ?? classConstructor.name
      return `${prefix}.${options.storageKey ?? propKeyString}`
    }

    Object.defineProperty(target, propertyKey, {
      get(this: Record<symbol, unknown>): unknown {
        const key = extracted(
          this.constructor as new (...args: unknown[]) => unknown
        )

        const stored = sessionStorage.getItem(key)
        if (stored !== null) {
          this[internalKey] = JSON.parse(stored) // intern speichern
          return this[internalKey]
        }

        this[internalKey] = options.default

        return this[internalKey]
      },

      set(this: Record<symbol, unknown>, value: unknown): void {
        const key = extracted(
          this.constructor as new (...args: unknown[]) => unknown
        )

        sessionStorage.setItem(key, JSON.stringify(value))
        this[internalKey] = value
      },

      enumerable: true,
      configurable: true,
    })
  }
}

export interface SessionSignalOptions<T> {
  key: string
  defaultValue: T
}

export function sessionSignal<T>(
  options: SessionSignalOptions<T>
): WritableSignal<T> {
  const stored = sessionStorage.getItem(options.key)

  const initialValue: T =
    stored === null ? options.defaultValue : (JSON.parse(stored) as T)

  const sig = signal<T>(initialValue)

  effect(() => {
    const value = sig()
    sessionStorage.setItem(options.key, JSON.stringify(value))
  })

  return sig
}

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  public constructor() {}

  public setItem<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value)
      localStorage.setItem(key, serializedValue)
    } catch (error) {
      console.error('Error saving to localStorage', error)
    }
  }

  public getItem<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key)
      if (item === null) {
        return null
      }
      return JSON.parse(item) as T
    } catch (error) {
      console.error('Error reading from localStorage', error)
      return null
    }
  }

  public removeItem(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error('Error removing from localStorage', error)
    }
  }

  public clear(): void {
    try {
      localStorage.clear()
    } catch (error) {
      console.error('Error clearing localStorage', error)
    }
  }

  public hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null
  }

  public getAllKeys(): string[] {
    try {
      return Object.keys(localStorage)
    } catch (error) {
      console.error('Error getting localStorage keys', error)
      return []
    }
  }
}
